import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { X, Sparkles, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

// Import generated images
import landscapeDreamy from "@/assets/gallery/landscape-dreamy.jpg";
import abstractFluid from "@/assets/gallery/abstract-fluid.jpg";
import portraitCyberpunk from "@/assets/gallery/portrait-cyberpunk.jpg";
import cosmosNebula from "@/assets/gallery/cosmos-nebula.jpg";
import abstractGeometric from "@/assets/gallery/abstract-geometric.jpg";
import abstractAcrylic from "@/assets/gallery/abstract-acrylic.jpg";
import abstractGradient from "@/assets/gallery/abstract-gradient.jpg";
import abstractMarble from "@/assets/gallery/abstract-marble.jpg";
import portraitFashion from "@/assets/gallery/portrait-fashion.jpg";
import landscapeMountains from "@/assets/gallery/landscape-mountains.jpg";
import animalCat from "@/assets/gallery/animal-cat.jpg";
import foodBurger from "@/assets/gallery/food-burger.jpg";
import scifiCity from "@/assets/gallery/scifi-city.jpg";
import fantasyForest from "@/assets/gallery/fantasy-forest.jpg";
import carSports from "@/assets/gallery/car-sports.jpg";
import natureSakura from "@/assets/gallery/nature-sakura.jpg";
import natureOcean from "@/assets/gallery/nature-ocean.jpg";
import fantasyRobot from "@/assets/gallery/fantasy-robot.jpg";
import landscapeBeach from "@/assets/gallery/landscape-beach.jpg";
import productPerfume from "@/assets/gallery/product-perfume.jpg";
import animePortrait from "@/assets/gallery/anime-portrait.jpg";
import interiorLiving from "@/assets/gallery/interior-living.jpg";

const galleryImages = [
  {
    src: landscapeDreamy,
    prompt: "Paysage onirique violet avec montagnes abstraites",
    category: "Paysages",
  },
  {
    src: abstractFluid,
    prompt: "Art abstrait fluide avec couleurs vibrantes",
    category: "Art abstrait",
  },
  {
    src: portraitCyberpunk,
    prompt: "Portrait futuriste néon style cyberpunk",
    category: "Portraits",
  },
  {
    src: cosmosNebula,
    prompt: "Cosmos galactique avec nébuleuses colorées",
    category: "Paysages",
  },
  {
    src: abstractGeometric,
    prompt: "Géométrie abstraite minimaliste",
    category: "Art abstrait",
  },
  {
    src: abstractAcrylic,
    prompt: "Vagues de couleur style peinture acrylique",
    category: "Art abstrait",
  },
  {
    src: abstractGradient,
    prompt: "Dégradé vibrant multicolore",
    category: "Art abstrait",
  },
  {
    src: abstractMarble,
    prompt: "Art liquide rose et rouge style marble",
    category: "Art abstrait",
  },
  {
    src: portraitFashion,
    prompt: "Portrait mode haute couture éclairage studio",
    category: "Portraits",
  },
  {
    src: landscapeMountains,
    prompt: "Paysage montagneux lever de soleil dramatique",
    category: "Paysages",
  },
  {
    src: animalCat,
    prompt: "Chat kawaii adorable aux grands yeux brillants",
    category: "Animaux",
  },
  {
    src: foodBurger,
    prompt: "Burger gourmet appétissant photographie culinaire",
    category: "Produits",
  },
  {
    src: scifiCity,
    prompt: "Ville futuriste cyberpunk sous la pluie néon",
    category: "Sci-Fi",
  },
  {
    src: fantasyForest,
    prompt: "Forêt enchantée avec champignons lumineux",
    category: "Fantasy",
  },
  {
    src: carSports,
    prompt: "Voiture de sport rouge luxueuse studio photo",
    category: "Produits",
  },
  {
    src: natureSakura,
    prompt: "Cerisier japonais en fleur jardin zen",
    category: "Paysages",
  },
  {
    src: natureOcean,
    prompt: "Récif corallien tropical sous-marin coloré",
    category: "Paysages",
  },
  {
    src: fantasyRobot,
    prompt: "Hibou mécanique steampunk engrenages dorés",
    category: "Fantasy",
  },
  {
    src: landscapeBeach,
    prompt: "Plage paradisiaque coucher de soleil palmiers",
    category: "Paysages",
  },
  {
    src: productPerfume,
    prompt: "Flacon de parfum luxueux photographie produit",
    category: "Produits",
  },
  {
    src: animePortrait,
    prompt: "Portrait anime cheveux bleus style japonais",
    category: "Portraits",
  },
  {
    src: interiorLiving,
    prompt: "Salon moderne minimaliste design scandinave",
    category: "Produits",
  },
];

const categories = ["Tous", "Portraits", "Paysages", "Art abstrait", "Fantasy", "Produits", "Sci-Fi", "Animaux"];

export const GalleryCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<typeof galleryImages[0] | null>(null);
  const [activeCategory, setActiveCategory] = useState("Tous");
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  const filteredImages = activeCategory === "Tous" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : filteredImages.length - 1));
  }, [filteredImages.length]);

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev < filteredImages.length - 1 ? prev + 1 : 0));
  }, [filteredImages.length]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setIsPaused(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const diff = startX - e.clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) handleNext();
      else handlePrev();
      setIsDragging(false);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setIsPaused(false), 2000);
  };

  // Reset index when category changes
  useEffect(() => {
    setActiveIndex(0);
  }, [activeCategory]);

  // Auto-scroll every 4 seconds
  useEffect(() => {
    if (isPaused || selectedImage) return;
    
    const interval = setInterval(() => {
      handleNext();
    }, 4000);
    
    return () => clearInterval(interval);
  }, [handleNext, isPaused, selectedImage]);

  return (
    <section className="py-20 relative overflow-hidden" id="gallery">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/30 to-background" />
      <div className="absolute top-1/4 right-0 w-[400px] h-[400px] bg-primary/5 blur-[120px] rounded-full" />
      <div className="absolute bottom-1/4 left-0 w-[400px] h-[400px] bg-accent/5 blur-[120px] rounded-full" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 glass-card border border-primary/30 rounded-full px-4 py-2 mb-4 text-sm font-medium text-foreground">
            <Sparkles className="w-4 h-4 text-primary" />
            Galerie • {galleryImages.length} créations
          </span>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 font-display">
            Des créations{" "}
            <span className="gradient-text-animated">extraordinaires</span>
          </h2>
          
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Découvrez la qualité de Floop à travers des exemples réels générés par notre IA.
          </p>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all duration-300",
                activeCategory === category
                  ? "bg-gradient-floop text-white shadow-lg shadow-primary/30"
                  : "glass-card border border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20"
              )}
            >
              {category}
            </button>
          ))}
        </div>

        {/* 3D Carousel */}
        <div 
          ref={carouselRef}
          className="relative h-[400px] md:h-[450px] perspective-1000 cursor-grab active:cursor-grabbing select-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onMouseEnter={() => setIsPaused(true)}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            {filteredImages.map((image, index) => {
              const offset = index - activeIndex;
              const absOffset = Math.abs(offset);
              
              if (absOffset > 3) return null;

              const translateX = offset * 240;
              const translateZ = -absOffset * 120;
              const rotateY = offset * -5;
              const scale = 1 - absOffset * 0.15;
              const opacity = 1 - absOffset * 0.3;
              const blur = absOffset * 2;

              return (
                <div
                  key={index}
                  className={cn(
                    "absolute w-56 h-72 md:w-60 md:h-80 rounded-2xl overflow-hidden transition-all duration-500 ease-out",
                    absOffset === 0 && "z-30",
                    absOffset === 1 && "z-20",
                    absOffset >= 2 && "z-10"
                  )}
                  style={{
                    transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
                    opacity,
                    filter: `blur(${blur}px)`,
                  }}
                  onClick={() => absOffset === 0 && setSelectedImage(image)}
                >
                  <img
                    src={image.src}
                    alt={image.prompt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    draggable={false}
                  />
                  
                  {absOffset === 0 && (
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <span className="inline-block text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full mb-1">
                          {image.category}
                        </span>
                        <p className="text-foreground font-medium text-xs line-clamp-2">
                          "{image.prompt}"
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {absOffset === 0 && (
                    <div className="absolute inset-0 rounded-2xl border-2 border-primary/50 pointer-events-none" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Navigation arrows */}
          <button
            onClick={(e) => { e.stopPropagation(); handlePrev(); setIsPaused(true); setTimeout(() => setIsPaused(false), 3000); }}
            className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 z-40 w-10 h-10 rounded-full glass-card border border-white/20 flex items-center justify-center text-foreground hover:border-primary/50 hover:text-primary transition-all duration-300"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <button
            onClick={(e) => { e.stopPropagation(); handleNext(); setIsPaused(true); setTimeout(() => setIsPaused(false), 3000); }}
            className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 z-40 w-10 h-10 rounded-full glass-card border border-white/20 flex items-center justify-center text-foreground hover:border-primary/50 hover:text-primary transition-all duration-300"
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center gap-1 mt-6">
          {filteredImages.map((_, index) => (
            <button
              key={index}
              onClick={() => { setActiveIndex(index); setIsPaused(true); setTimeout(() => setIsPaused(false), 3000); }}
              aria-label={`Go to slide ${index + 1}`}
              className="w-6 h-6 flex items-center justify-center"
            >
              <span
                className={cn(
                  "rounded-full transition-all duration-300",
                  index === activeIndex
                    ? "w-6 h-1.5 bg-gradient-floop"
                    : "w-1.5 h-1.5 bg-white/20 hover:bg-white/40"
                )}
              />
            </button>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Link to="/create">
            <Button className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-6 py-5 text-base group shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40 transition-all duration-300 hover:scale-105">
              Créer ma première image
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-background/95 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="relative max-w-3xl w-full glass-card border border-white/10 rounded-2xl overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full glass-card border border-white/20 flex items-center justify-center text-foreground hover:text-primary hover:border-primary/50 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="grid md:grid-cols-2">
              <img
                src={selectedImage.src}
                alt={selectedImage.prompt}
                className="w-full aspect-square md:aspect-auto md:h-full object-cover"
                loading="lazy"
              />
              <div className="p-6 flex flex-col justify-center">
                <span className="inline-block text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full w-fit mb-3">
                  {selectedImage.category}
                </span>
                <h3 className="text-lg font-bold text-foreground mb-3 font-display">Prompt utilisé</h3>
                <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                  "{selectedImage.prompt}"
                </p>
                <Link to="/create">
                  <Button className="bg-gradient-floop hover:opacity-90 text-white rounded-full group w-full sm:w-auto" size="sm">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Créer une image similaire
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
